import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeoProxyPromocodesType } from '../../../../../../common/constants/types';
import { useCurrentTabShownCurrency, usePriceConfig, workspaceContext } from '../../../../../state';
import { IDiscountShort, IPeroid, IPromoGifts, IPromocodesTraffic } from '../../../../pricing/interfaces';
import { getDiscountText } from '../utils';

interface IPromoActivatedGifts {
  promoGiftsData: IPromoGifts;
}

const PromoActivatedGifts: FC<IPromoActivatedGifts> = (props) => {
  const { promoGiftsData } = props;

  const [giftsData, setGiftsData] = useState<IPromoGifts>({} as IPromoGifts);

  const workspace = useContext(workspaceContext);
  const priceConfig = usePriceConfig();
  const currency = useCurrentTabShownCurrency();

  const { t: translation } = useTranslation();

  useEffect(() => {
    setGiftsData({} as IPromoGifts);
  }, [workspace.id]);

  useEffect(() => {
    setGiftsData(promoGiftsData);
  }, [promoGiftsData]);

  const renderDiscount = (discount: IDiscountShort): JSX.Element | null => {
    const { active, discountType, value } = discount;
    const isNeedToShowDiscount = active && value;
    if (!isNeedToShowDiscount) {
      return null;
    }

    return (
      <div>
        {translation('billing.promocode.discountOnMonthTitle')}
        :
        &nbsp;
        {getDiscountText({ value, discountType, priceConfig, currency })}
      </div>
    );
  };

  const renderTraffic = (traffic: IPromocodesTraffic): JSX.Element | null => {
    const { type, quantity } = traffic;
    if (!quantity) {
      return null;
    }

    return (
      <div>
        {quantity}
        {translation('billing.promocode.proxyGB')}
        &nbsp;
        {translateType(type)}
        &nbsp;
        {translation('billing.promocode.proxies')}
      </div>
    );
  };

  const renderPeriod = (period: IPeroid): JSX.Element | null => {
    const { unit, amount } = period;
    if (!amount) {
      return null;
    }

    return (
      <div>
        {translation('billing.promocode.periodGift')}
        &nbsp;
        {parseAndTranslatePeriodUnit(unit, amount)}
      </div>
    );
  };

  const renderActivatedGifts = (): JSX.Element | null => {
    const {
      discount = { active: false, discountType: 'percent', value: 0 },
      traffic = { type: GeoProxyPromocodesType.Resident, quantity: 0 },
      period = { unit: 'd', amount: 0 },
    } = giftsData;

    const isNeedToShowGifts = discount.value || traffic.quantity || period.amount;
    if (!isNeedToShowGifts) {
      return null;
    }

    return (
      <div>
        <div style={{ marginBottom: 10 }}>
          {translation('billing.promocode.activatedGiftsTitle')}
          :
        </div>
        {renderDiscount(discount)}
        {renderTraffic(traffic)}
        {renderPeriod(period)}
      </div>
    );
  };

  const parseAndTranslatePeriodUnit = (unit: string, amount: number): string => {
    switch (unit.toLowerCase()) {
      case 'y':
        return translation('billing.promocode.periodGiftYears', { count: amount });
      case 'm':
        return translation('billing.promocode.periodGiftMonths', { count: amount });
      case 'w':
        return translation('billing.promocode.periodGiftWeeks', { count: amount });
      default:
        return translation('billing.promocode.periodGiftDays', { count: amount });
    }
  };

  const translateType = (type: GeoProxyPromocodesType): string => {
    switch (type) {
      case GeoProxyPromocodesType.Mobile:
        return translation('billing.promocode.proxyTypeMobile');
      case GeoProxyPromocodesType.DataCenter:
        return translation('billing.promocode.proxyTypeDataCenter');
      default:
        return translation('billing.promocode.proxyTypeResidential');
    }
  };

  return renderActivatedGifts();
};

export default PromoActivatedGifts;
